import React, { createRef } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import merge from 'lodash/merge';
import Promise from 'promise';
import PropTypes from 'prop-types';
import { Sticky, StickyContainer } from 'react-sticky';
import format from 'date-fns/format';

import {
    Alert,
    CloseButton,
    Spacer,
    TextButton,
} from '@zillow/constellation';
import { Row, Col } from '@zillow/zrs-comet';

// rmw context
import { RMWContext } from 'app/shared/providers/RMWContext';

import assign from 'lodash/assign';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';

// constants
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import NotificationConstants from 'app/shared/constants/NotificationConstants';
import WebSocketMessageNameConstants from 'app/shared/constants/WebSocketMessageNameConstants';
import StringConstants from 'app/shared/constants/StringConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';
import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import ErrorMessageConstants from 'app/shared/constants/ErrorMessageConstants';
import Constants from 'app/shared/constants';

// Posting Path Components
import DuplicateListingDetails from 'app/shared/components/DuplicateListingDetails';
import PDPContentSkeleton from 'app/shared/components/skeletons/PDPContentSkeleton';
import SingleFamilyForm from 'app/shared/components/postingPath/SingleFamilyForm';
import MultiFamilyForm from 'app/shared/components/postingPath/MultiFamilyForm';
import NotViewableMessage from 'app/shared/components/postingPath/NotViewableMessage';
import NotViewableContent from 'app/shared/components/postingPath/NotViewableContent';
import FeedListingSourceBanner from 'app/shared/components/postingPath/FeedListingSourceBanner';
import ListingActivationTerms from 'app/shared/components/ListingActivationTerms';

// Actions
import AddressActions from 'app/shared/actions/AddressActions';
import ModalActions from 'app/shared/actions/ModalActions';
import PostingActions from 'app/shared/actions/PostingActions';
import PropertiesActions from 'app/shared/actions/PropertiesActions';
import SingleFamilyEnterpriseActions from 'app/shared/actions/SingleFamilyEnterpriseActions';
import UserActions from 'app/shared/actions/UserActions';
import PaidInclusionActions from 'app/shared/actions/PaidInclusionActions';
import TermsAndConditionsActions from 'app/shared/actions/TermsAndConditionsActions';

import appLogger from 'app/shared/loggers/appLogger';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import NotificationModule, { NOTIFICATION_OPTION } from 'app/client/workers/notificationModule';
import browserUtils from 'app/shared/utils/browserUtils';
import EditListingPageInitState from 'app/shared/containers/init-states/EditListingPageInitState';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import ConnectedListingFooter from 'app/shared/components/ConnectedListingFooter';

// utils
import accessUtils from 'app/shared/utils/accessUtils';
import getLocationRestrictionErrorMessage from 'app/shared/utils/getLocationRestrictionErrorMessageUtils';
import propertyUtils from 'app/shared/utils/propertyUtils';
import stringHelper from 'app/shared/utils/stringHelper';

// Selectors
import {
    AppSelectors,
    PropertiesSelectors,
    UserSelectors,
    RoutingSelectors,
} from 'app/shared/selectors';
import PaidInclusionSelectors from 'app/shared/selectors/PaidInclusionSelectors';
import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';

import {
    requiredFieldNames,
    singleFamilyRequiredFields,
    multiFamilyRequiredFields,
    requiredPropertyErrorMessages,
    requiredFloorplanErrorMessages,
    requiredUnitErrorMessages,
    unitErrorObjBlank,
    floorplanErrorObjBlank,
    minPhotosMFH,
    discriminatoryContentCheckFieldNames,
    singleFamilyDiscriminatoryContentCheckFields,
    multiFamilyDiscriminatoryContentCheckFields,
} from 'app/shared/constants/EditListingPageConstants';

import {
    StyledActivationErrorStickyDiv,
    StyledActivationErrorToastDiv,
    StyledMainContentDiv,
    StyledNotification,
    StyledNotificationToastContainerDiv,
    StyledPostingFormDiv,
    StyledToast,
} from './StyledComponents';

const { startCaseRemoveSpace } = stringHelper;
const { GA_EVENTS, SUFFIX } = AnalyticsConstants;
const { appendSuffix, track } = analyticsUtils;
const { SYNDICATION_OVERVIEW_STATUS, LISTING_STATUS } = PropertyConstants;

const propTypes = {
    listing: PropTypes.object.isRequired,
    updateListing: PropTypes.func,
    isArchived: PropTypes.bool.isRequired,
    canAccessPayments: PropTypes.bool.isRequired,
    isEditListingFocused: PropTypes.bool,
};

const defaultProps = {
    updateListing: () => {},
    isEditListingFocused: false,
};

//  TODO: rename to PropertyDetailsListingPage or ListingPage
class EditListingPage extends React.Component {
    static contextType = RMWContext;

    constructor(props) {
        super(props);

        this.state = this.getInitialState();

        // bind methods
        this.updateListingPhotoProcessingStatus = this.updateListingPhotoProcessingStatus.bind(this);
        this.updateFormData = this.updateFormData.bind(this);
        this.getNotificationType = this.getNotificationType.bind(this);
        this.updateOnFocus = this.updateOnFocus.bind(this);
        this.updateStatusMessage = this.updateStatusMessage.bind(this);
        this.immediateUpdateAndSave = this.immediateUpdateAndSave.bind(this);
        this.isDisplayingFeedConnectPreActivateModal = this.isDisplayingFeedConnectPreActivateModal.bind(this);
        this.updateErrorObj = this.updateErrorObj.bind(this);
        this.updateParentFloorplanPrice = this.updateParentFloorplanPrice.bind(this);
        this.savePropertyData = this.savePropertyData.bind(this);
        this.saveFloorplanData = this.saveFloorplanData.bind(this);
        this.clearEmptyFloorplans = this.clearEmptyFloorplans.bind(this);
        this.displayFeedConnectPreActivateModal = this.displayFeedConnectPreActivateModal.bind(this);
        this.validateFloorplan = this.validateFloorplan.bind(this);
        this.validateFloorplans = this.validateFloorplans.bind(this);
        this.validateUnit = this.validateUnit.bind(this);
        this.getActiveFloorplanOrUnitCount = this.getActiveFloorplanOrUnitCount.bind(this);
        this.getUpsellType = this.getUpsellType.bind(this);
        this.validateFields = this.validateFields.bind(this);
        this.validateDiscriminatoryContentErrors = this.validateDiscriminatoryContentErrors.bind(this);
        this.updateActivationErrors = this.updateActivationErrors.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.openAddPropertyModal = this.openAddPropertyModal.bind(this);
        this.setSuccessfulRenewalData = this.setSuccessfulRenewalData.bind(this);
        this.setFailedRenewalData = this.setFailedRenewalData.bind(this);
        this.showSiftScience = this.showSiftScience.bind(this);
        this.renderlistingDetails = this.renderlistingDetails.bind(this);
        this.renderNotViewableContent = this.renderNotViewableContent.bind(this);
        this.scrollToRef = this.scrollToRef.bind(this);
        this.renderRestrictionAddressMessageAlert = this.renderRestrictionAddressMessageAlert.bind(this);
        this.handleVerifyPaymentAndActivateListingResponse =
            this.handleVerifyPaymentAndActivateListingResponse.bind(this);
        this.getApplicationsEnabledStatus = this.getApplicationsEnabledStatus.bind(this);
        this.handleEnableLeases = this.handleEnableLeases.bind(this);

        this.forRentByRef = createRef();
        this.contactNameRef = createRef();
        this.descriptionRef = createRef();
        this.rentPriceRef = createRef();
        this.contactHoursRef = createRef();
        this.leaseDurationRef = createRef();
        this.contactPhoneRef = createRef();
        this.photosRef = createRef();
        this.floorplansRef = createRef();
        this.listingTitleRef = createRef();
        this.activationErrorsRef = createRef();
        this.isApplicationsAcceptedRef = createRef();

        this.refMap = new Map([
            [requiredFieldNames.listingTitle, this.listingTitleRef],
            [requiredFieldNames.description, this.descriptionRef],
            [requiredFieldNames.price, this.rentPriceRef],
            [requiredFieldNames.forRentBy, this.forRentByRef],
            [requiredFieldNames.contactName, this.contactNameRef],
            [requiredFieldNames.contactHours, this.contactHoursRef],
            [requiredFieldNames.leaseDuration, this.leaseDurationRef],
            [requiredFieldNames.contactPhone, this.contactPhoneRef],
            [requiredFieldNames.photos, this.photosRef],
            [requiredFieldNames.isApplicationsAccepted, this.isApplicationsAcceptedRef],
            [requiredFieldNames.floorplans, this.floorplansRef],
        ]);
    }

    getInitialState() {
        // update default form data with listing prop
        const initState = { ...EditListingPageInitState };

        const { listing } = this.props;

        initState.formData = merge({}, initState.formData, listing);
        initState.activateListingButtonText = 'Publish listing';
        initState.showAboveFold = true;
        initState.isAddingUnit = false;
        initState.activateButtonClicked = false;
        // isPaymentValid - is the listing within the payment period
        // add to state as the value can change after paid listing checkout
        initState.isPaymentValid = Boolean(listing.paidInclusion && listing.paidInclusion.isPaymentValid);
        initState.hasRestrictionErrorMessage = false;

        return initState;
    }

    componentDidMount() {
        const {
            currentProperty,
            email,
            listing,
            propertyId,
            sessionToken,
        } = this.props;
        const { formData, errorObj } = this.state;
        const { isAddressValid } = currentProperty;

        this.setState({ isLoading: true });

        const canUpdateApplications = accessUtils.canUpdateApplications({
            restrictions: formData.restrictions,
        });

        // Update the number of floorplans in corresponding errorObj
        const updateErrorObj = { ...errorObj };
        const updateErrorObjFloorplans = [...errorObj.floorplans];
        formData.floorplans.forEach((floorplan) => {
            updateErrorObjFloorplans.push({
                ...floorplanErrorObjBlank,
                units: floorplan.units.map(() => ({ ...unitErrorObjBlank })),
            });
        });
        updateErrorObj.floorplans = updateErrorObjFloorplans;

        const baseStateUpdate = {
            canUpdateApplications,
            isLoading: false,
            propertyId,
            errorObj: updateErrorObj,
        };
        const {
            city,
            street,
            zip,
            state,
            unit,
            listingTypeCode,
            propertyTypeCode,
            listingAlias,
        } = formData;

        if (!formData.active) {
            this.getAddressRestrictions({
                street, city, state, zip, unit, listingTypeCode, propertyTypeCode, listingAlias,
            });
        }
        this.setState({ ...baseStateUpdate });
        this.showSiftScience(email, sessionToken);

        // clear empty floor plans if there are more than 1
        if (formData.floorplans.length > 1) {
            this.clearEmptyFloorplans();
        }

        const { isEnabled, isPaymentRequired } = listing.paidInclusion || {};

        if (formData.active) {
            if (isEnabled) {
                if (isPaymentRequired) {
                    this.dispatchGAEvent(GA_EVENTS.LISTING_PAGE_VIEW_ACTIVE_NUCLEUS_PAID);
                } else {
                    this.dispatchGAEvent(GA_EVENTS.LISTING_PAGE_VIEW_ACTIVE_NUCLEUS_FREE);
                }
            } else {
                this.dispatchGAEvent(GA_EVENTS.LISTING_PAGE_VIEW_ACTIVE_NON_NUCLEUS);
            }
        } else if (isEnabled) {
            if (isPaymentRequired) {
                this.dispatchGAEvent(GA_EVENTS.LISTING_PAGE_VIEW_OFFMARKET_NUCLEUS_PAID);
            } else {
                this.dispatchGAEvent(GA_EVENTS.LISTING_PAGE_VIEW_OFFMARKET_NUCLEUS_FREE);
            }
        } else {
            this.dispatchGAEvent(GA_EVENTS.LISTING_PAGE_VIEW_OFFMARKET_NON_NUCLEUS);
        }

        if (isAddressValid === false) {
            this.openAddPropertyModal();
        }

        window.rentalManagerApiWebSocket.addEventListener('message', (message) => {
            this.handleRentalManagerApiWebSocket(message);
        });
    }

    componentWillReceiveProps(nextProps) {
        const { listing } = nextProps;
        if (listing) {
            this.setState({ formData: listing });

            if (listing.listingAlias !== this.props.listing.listingAlias) {
                this.setState({
                    hasRestrictionErrorMessage: false,
                    errorMessage: '',
                    paidListingExists: false,
                });

                const {
                    city,
                    street,
                    zip,
                    state,
                    unit,
                    listingTypeCode,
                    propertyTypeCode,
                    listingAlias,
                } = listing;

                this.getAddressRestrictions({
                    street, city, state, zip, unit, listingTypeCode, propertyTypeCode, listingAlias,
                });
            }
        }
        if (nextProps.propertyId !== this.props.propertyId) {
            this.setState({ propertyId: nextProps.propertyId });
        }
        if (nextProps.defaultPaymentMethod && nextProps.defaultPaymentMethod !== this.props.defaultPaymentMethod) {
            this.props.dispatch(ModalActions.updateModal({
                modalType: ModalConstants.PAID_INCLUSION_PAYMENT_MODAL,
                handleVerifyPaymentAndActivateListingResponse: this.handleVerifyPaymentAndActivateListingResponse,
            }));
        }
        if (nextProps.paidInclusionListingCannotBeActivatedError) {
            this.props.dispatch(ModalActions.showModal({
                modalType: ModalConstants.ERROR_MODAL,
                header: 'Error',
                error: nextProps.paidInclusionListingCannotBeActivatedError,
            }));
        }
        if (nextProps.isPaymentValid != null && nextProps.isPaymentValid !== this.state.isPaymentValid) {
            this.setState({ isPaymentValid: nextProps.isPaymentValid });
        }
    }

    componentWillUnmount() {
        // remove sift science js
        browserUtils.removeElementById('sift-science');
        browserUtils.removeElementById('sift-science-tracker');
        if (this.notificationTimer) {
            clearTimeout(this.notificationTimer);
        }

        window.rentalManagerApiWebSocket.removeEventListener('message', this.handleRentalManagerApiWebSocket);
    }

    displayFeedConnectPreActivateModal() {
        const {
            appConfig,
            cookieEnabledServices,
            currentProperty,
            dispatch,
        } = this.props;
        const { formData } = this.state;
        const { singleFamilyContract, topDupesDetails } = formData;
        const { listingAlias, isFeed } = currentProperty;

        const canFeedConnectCreateListing = accessUtils.canFeedConnectCreateListing({
            appConfig,
            cookieEnabledServices,
        });

        const mustSetActiveContract = canFeedConnectCreateListing && !isFeed && singleFamilyContract.isRequired
            && !singleFamilyContract.isActive;
        const contractPromise = mustSetActiveContract
            ? dispatch(SingleFamilyEnterpriseActions.getContracts())
            : Promise.resolve([singleFamilyContract.details]);

        contractPromise.then((contracts) => {
            this.dispatchGAEvent({
                ...GA_EVENTS.PRE_ACTIVATE_MODAL_SF_CONTRACTS,
                action: `${GA_EVENTS.PRE_ACTIVATION_MODAL_SF_CONTRACTS.action}${contracts.length}`,
            });
            if (mustSetActiveContract && contracts.length === 0) {
                this.updateStatusMessage(ErrorMessageConstants.SINGLE_FAMILY_NO_ACTIVE_CONTRACT,
                    NotificationConstants.ERROR);
            } else {
                this.props.dispatch(ModalActions.showModal({
                    modalType: ModalConstants.PRE_ACTIVATION_MODAL,
                    header: 'Publish this listing?',
                    hasDupes: topDupesDetails && topDupesDetails.dupes.length > 0,
                    isFluid: true,
                    contracts,
                    mustSetActiveContract,
                    onCancel: () => {
                        this.dispatchGAEvent(GA_EVENTS.PRE_ACTIVATION_MODAL_DISMISS);
                        this.props.dispatch(ModalActions.hideModal());
                    },
                    onSubmit: async (newCustomerId = null) => {
                        const setNewActiveContractPromise = mustSetActiveContract
                            ? dispatch(SingleFamilyEnterpriseActions.setCustomerId(listingAlias, newCustomerId))
                            : Promise.resolve();
                        try {
                            await setNewActiveContractPromise;
                        } catch {
                            const errMessage = 'Unable to publish this property due to lack of an active contract. '
                                + 'Please contact rentalshelp@zillowgroup.com.';
                            this.updateStatusMessage(errMessage, NotificationConstants.ERROR);
                            this.props.dispatch(ModalActions.hideModal());
                            return;
                        }
                        this.props.dispatch(ModalActions.hideModal());
                        this.handleSubmit();
                    },
                    onOpen: () => this.dispatchGAEvent(GA_EVENTS.PRE_ACTIVATION_MODAL_DISPLAY),
                    onClose: () => this.dispatchGAEvent(GA_EVENTS.PRE_ACTIVATION_MODAL_DISMISS),
                }));
            }
        }).catch(() => this.updateStatusMessage(ErrorMessageConstants.SINGLE_FAMILY_CANNOT_RETRIEVE_CONTRACTS,
            NotificationConstants.ERROR));
    }

    handleRentalManagerApiWebSocket(message) {
        // update listing status section with syndication status updates.
        let data;
        try {
            data = JSON.parse(message.data);
        } catch (error) {
            return;
        }
        if (data.name !== WebSocketMessageNameConstants.LISTING_SYNDICATION_STATUS) {
            return;
        }
        const { payload } = data;
        const { feedId, feedListingId, listingAlias } = payload;
        const { listingId } = this.props;
        // Don't do anything if the message is for a different listing
        if (listingId !== listingAlias && listingId !== propertyUtils.getListingId(feedId, feedListingId)) {
            return;
        }
        const syndicationPayload = payload.syndication;
        const { formData: { syndication } } = this.state;
        const syndicationCopy = Object.assign({}, syndication);
        syndicationCopy.hotpads = syndicationPayload.hotpads;
        syndicationCopy.trulia = syndicationPayload.trulia;
        syndicationCopy.zillow = syndicationPayload.zillow;
        syndicationCopy.universal = syndicationPayload.universal;
        syndicationCopy.overview = syndicationPayload.overview;
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                syndication: syndicationCopy,
            },
        }));
    }

    getAddressRestrictions({
        street, zip, city, unit, state, listingTypeCode, propertyTypeCode, listingAlias,
    }) {
        const { dispatch } = this.props;
        const address = `${street}, ${city}, ${state} ${zip}`;

        return dispatch(
            AddressActions.getRestrictions({
                address,
                unit,
                listingTypeCode,
                listingAlias,
                propertyTypeCode,
            }),
        )
            .then((response) => {
                const { paidListingExists } = response;
                const isMultifamily = PropertyConstants.PROPERTY_TYPES.COMMUNITY
                    === PropertyConstants.PROPERTY_TYPES_BY_CODE[propertyTypeCode];
                const errorMessage = getLocationRestrictionErrorMessage(response, isMultifamily);

                if (errorMessage) {
                    this.setState({
                        hasRestrictionErrorMessage: true,
                        errorMessage,
                        paidListingExists,
                    });
                }
            });
    }

    dispatchGAEvent = (gaEventObj) => {
        const {
            listing: { aliasEncoded },
            dispatch,
            currentProperty: {
                leadGuarantee: {
                    isEligible: isEligibleForLeadGuarantee,
                },
            },
        } = this.props;

        const replaceLabelWithAliasEncoded = gaEventObj.label === 'ListingManagement' &&
                                             gaEventObj.category === 'PostingPath';
        const gaEvent = {
            ...gaEventObj,
            ...(replaceLabelWithAliasEncoded) && { label: aliasEncoded },
            ...(isEligibleForLeadGuarantee) && {
                action: appendSuffix(gaEventObj.action, SUFFIX.LEAD_GUARANTEE),
            },
        };

        dispatch(track.GAEventV2(gaEvent));
    }

    updateListingPhotoProcessingStatus(newData) {
        this.setState({ isListingPhotoProcessing: newData });
    }

    updateFormData(newData) {
        const { updateListing } = this.props;
        const { formData } = this.state;
        updateListing({ ...formData, ...newData });
    }

    getNotificationType() {
        const { brand } = this.context;
        const { savedStatus } = this.state;

        let notificationStatus = savedStatus || 'info';

        // if brand is zillow set success type to be info type
        if (brand === StringConstants.UNFORMATTED_BRAND_NAMES.zillow) {
            notificationStatus =
                notificationStatus === NotificationConstants.SUCCESS ? NotificationConstants.INFO : notificationStatus;
        }

        return notificationStatus.toLowerCase();
    }

    updateOnFocus(field, valueToUpdate) {
        const value = valueToUpdate || '';

        const updateInputOnFocus = {
            field,
            value,
        };

        this.setState({ inputOnFocus: updateInputOnFocus });
    }

    updateStatusMessage(message, status = null) {
        this.setState({
            statusMessage: message,
            savedStatus: status,
        });
    }

    setSuccessfulRenewalData(expirationDate) {
        this.updateFormData({ expirationDate });
        this.setState({ formErrorMessage: '' });
    }

    setFailedRenewalData(formErrorMessage) {
        this.setState({
            formErrorMessage,
        });
    }

    immediateUpdateAndSave(
        fieldName,
        value,
        {
            componentGroupName = '', category = null, preserveFieldName = false, formDataName = null,
        } = {},
    ) {
        // In the case of radio buttons and checkboxes, immediately update!
        const { formData } = this.state;

        if (formDataName !== null) {
            if (category !== null) {
                this.setState({
                    formData: assign({}, formData, {
                        buildingAttributes: {
                            ...formData.buildingAttributes,
                            [category]: {
                                ...formData.buildingAttributes[category],
                                [formDataName]: value,
                            },
                        },
                    }),
                });
            } else {
                this.setState({
                    formData: assign({}, formData, {
                        buildingAttributes: {
                            ...formData.buildingAttributes,
                            [formDataName]: value,
                        },
                    }),
                });
            }
        } else {
            const data = {
                [fieldName]: value,
            };

            this.updateFormData(data);
        }

        return this.savePropertyData(fieldName, value, {
            componentGroupName,
            preserveFieldName,
        });
    }

    isDisplayingFeedConnectPreActivateModal = () => {
        const { appConfig, cookieEnabledServices, currentProperty } = this.props;
        const { formData } = this.state;
        const { singleFamilyContract } = formData;
        const { isFeed } = currentProperty;

        const canFeedConnectCreateListing = accessUtils.canFeedConnectCreateListing({
            appConfig,
            cookieEnabledServices,
        });

        // @TODO The publish button should be disabled for MF feed listings, so it's fine to conclude isFeed is for
        // SFH only. Remove once isSingleFamilyEnterpriseListing is returned properly on the BE
        const shouldDisplayPreActivateModal = (canFeedConnectCreateListing && singleFamilyContract.isRequired)
             || isFeed;

        if (shouldDisplayPreActivateModal) {
            if (this.validateFields(true)) {
                return true;
            }
            this.displayFeedConnectPreActivateModal();
        }

        return shouldDisplayPreActivateModal;
    };

    isListingFree() {
        const { listing } = this.props;
        const { paidInclusion } = listing;
        const {
            isEnabled: isListingInNucleusMarket,
            isPaymentRequired,
        } = paidInclusion || {};

        return !isPaymentRequired || !isListingInNucleusMarket;
    }

    updateErrorObj(errMsg) {
        const { errorObj, activateButtonClicked } = this.state;
        const updateErrorObj = { ...errorObj, ...errMsg };
        this.setState({ errorObj: updateErrorObj }, () => {
            if (activateButtonClicked) {
                this.validateFields();
            }
        });
    }

    savePropertyData(
        fieldName,
        value,
        { componentGroupName = '', preserveFieldName = false } = {},
    ) {
        const { dispatch, userApplicationsEnabled } = this.props;
        const { inputOnFocus, propertyId } = this.state;
        // This prevents us from hitting the API if no data has changed.
        if (
            inputOnFocus.field === fieldName &&
            inputOnFocus.value.toString() === value.toString()
        ) {
            return Promise.resolve(true);
        }

        this.setState({ inputOnFocus: {} });
        this.updateStatusMessage('Saving...');

        const dataToSave = {
            fieldName,
            value,
            propertyId,
        };

        // TODO: check sending correct data to save
        return dispatch(
            PostingActions.property.edit(dataToSave, {
                componentGroupName,
                preserveFieldName,
            }),
        )
            .then(() => {
                this.updateStatusMessage('All data saved!', NotificationConstants.SUCCESS);

                if (!userApplicationsEnabled && fieldName === 'isApplicationsAccepted' && value) {
                    dispatch(UserActions.updateUserInfo({ applicationsEnabled: true }));
                }

                if (this.state.showReadyToPublishMessage) {
                    this.updateStatusMessage('Your listing is ready to be published!', NotificationConstants.SUCCESS);
                    this.setState({ showReadyToPublishMessage: false });
                }

                this.updateErrorObj({ [fieldName]: null });

                return Promise.resolve(true);
            })
            .catch((err) => {
                this.updateStatusMessage('Please fix the inputs highlighted in red.', NotificationConstants.ERROR);
                this.updateErrorObj({ [fieldName]: err });
                return Promise.reject(err);
            });
    }

    saveFloorplanData(fieldName, value, floorplanIndex = 0, unitIndex = null) {
        const { dispatch, listing: { aliasEncoded } } = this.props;
        const { formData, errorObj, inputOnFocus } = this.state;
        const floorplans = formData.floorplans.slice();

        // This prevents us from hitting the API if no data has changed.
        if (
            inputOnFocus.field === fieldName &&
            inputOnFocus.value.toString() === value.toString()
        ) {
            this.setState({ inputOnFocus: {} });
            return;
        }

        const floorPlan = floorplans[floorplanIndex];
        const floorPlanId = unitIndex === null
            ? floorPlan.floorPlanId
            : floorPlan.units[unitIndex] && floorPlan.units[unitIndex].unitId;

        const updateErrorObj = { ...errorObj };
        const updateFloorplanErrors = updateErrorObj.floorplans;

        const dataToSave = {
            fieldName,
            value,
            floorPlanId,
        };

        // Cannot delete floorplan / unit name for community buildings
        if (fieldName === 'name' && value === '' && formData.isCommunity) {
            if (unitIndex === null) {
                updateFloorplanErrors[floorplanIndex][fieldName] = requiredFloorplanErrorMessages.name;
            } else if (updateFloorplanErrors[floorplanIndex].units[unitIndex]) {
                updateFloorplanErrors[floorplanIndex].units[unitIndex][fieldName] = requiredUnitErrorMessages.name;
            }
            this.updateErrorObj(updateErrorObj);
            return;
        }

        this.updateStatusMessage('Saving...');

        dispatch(PostingActions.floorplan.edit(dataToSave))
            .then(() => {
                this.updateStatusMessage('All data saved!', NotificationConstants.SUCCESS);

                if (unitIndex === null) {
                    updateFloorplanErrors[floorplanIndex][fieldName] = null;
                } else if (updateFloorplanErrors[floorplanIndex].units[unitIndex]) {
                    updateFloorplanErrors[floorplanIndex].units[unitIndex][fieldName] = null;
                }
                if (fieldName === 'active' && value === true) {
                    updateFloorplanErrors.forEach((floorplanErrors, i) => {
                        updateFloorplanErrors[i].active = null;
                        floorplanErrors.units.forEach((unitErrors, j) => {
                            updateFloorplanErrors[i].units[j].active = null;
                        });
                    });
                }
                if (fieldName === 'active') {
                    if (unitIndex !== null) {
                        dispatch(track.GAEventV2(
                            AnalyticsConstants.GA_EVENTS.TOGGLE_AVAILABILITY_UNIT,
                            { action: startCaseRemoveSpace(`${value ? '' : 'de'}activateUnit`) },
                            { label: aliasEncoded }
                        ));
                    } else {
                        dispatch(track.GAEventV2(
                            AnalyticsConstants.GA_EVENTS.TOGGLE_AVAILABILITY_FLOORPLAN,
                            { action: startCaseRemoveSpace(`${value ? '' : 'de'}activateFloorplan`) },
                            { label: aliasEncoded }
                        ));
                    }
                }
                this.updateErrorObj(updateErrorObj);
                if (fieldName === 'price' && unitIndex !== null) {
                    this.updateParentFloorplanPrice(floorPlan.floorPlanId);
                }
            })
            .catch((res) => {
                this.updateStatusMessage('Please fix the inputs highlighted in red.', NotificationConstants.ERROR);

                if (unitIndex === null) {
                    updateFloorplanErrors[floorplanIndex][fieldName] = res;
                } else if (updateFloorplanErrors[floorplanIndex].units[unitIndex]) {
                    updateFloorplanErrors[floorplanIndex].units[unitIndex][fieldName] = res;
                }
                this.updateErrorObj(updateErrorObj);
            });
    }

    updateParentFloorplanPrice(floorPlanId) {
        return this.props.dispatch(PostingActions.floorplan.get(floorPlanId))
            .then((newFloorPlan) => {
                const { formData } = this.state;
                const floorplans = cloneDeep(formData.floorplans);
                const floorPlanIndex = floorplans.findIndex(item => item.floorPlanId === floorPlanId);
                const previousFloorPlan = floorplans[floorPlanIndex];
                if (!previousFloorPlan || !newFloorPlan) {
                    return;
                }
                if (previousFloorPlan.lowPrice !== newFloorPlan.lowPrice
                    || previousFloorPlan.highPrice !== newFloorPlan.highPrice) {
                    floorplans[floorPlanIndex].lowPrice = newFloorPlan.lowPrice;
                    floorplans[floorPlanIndex].highPrice = newFloorPlan.highPrice;
                    this.updateFormData({ floorplans });
                }
            })
            .catch((res) => {
                console.error('Error getting the floorplan', res);
            });
    }

    clearEmptyFloorplans() {
        const { dispatch } = this.props;
        const { formData } = this.state;
        const floorplans = formData.floorplans.slice();
        let willRemoveFloorplan = false;

        for (let i = 0; i < floorplans.length; i++) {
            const floorplan = floorplans[i];
            const {
                name,
                beds,
                baths,
                price,
                sqft,
                floorPlanPhotos,
                layoutPhoto,
                unitAmenities,
                floorPlanId,
            } = floorplan;

            // if all properties are empty
            if (
                isEmpty(name) &&
                beds === '' &&
                baths === '' &&
                price === '' &&
                sqft === '' &&
                isEmpty(floorPlanPhotos) &&
                isEmpty(layoutPhoto) &&
                isEmpty(unitAmenities)
            ) {
                // update flag
                willRemoveFloorplan = true;

                // delete floorplan
                dispatch(PostingActions.property.removeFloorplan(floorPlanId)).then(
                    () => {
                        // find target index
                        const targetIndex = floorplans
                            .map(item => item.floorPlanId)
                            .indexOf(floorPlanId);

                        // remove from array
                        floorplans.splice(targetIndex, 1);

                        // update state
                        this.updateFormData({ floorplans });
                    },
                );
            }
        }

        return willRemoveFloorplan;
    }

    validateFloorplan(floorplanIndex, validateUnits = true) {
        return this.validateFloorplans(floorplanIndex, validateUnits);
    }

    validateFloorplans(filteredFloorplanIndex = null, validateUnits = false) {
        const { formData, errorObj } = this.state;
        const updateErrorObj = cloneDeep(errorObj);
        let isValidFloorplans = true;

        formData.floorplans.forEach((floorplan, floorplanIndex) => {
            if (filteredFloorplanIndex !== null && filteredFloorplanIndex !== floorplanIndex) {
                return;
            }
            if (!floorplan.name) {
                updateErrorObj.floorplans[floorplanIndex].name = requiredFloorplanErrorMessages.name;
                isValidFloorplans = false;
            }

            if (floorplan.beds === '' || typeof floorplan.beds === 'undefined') {
                updateErrorObj.floorplans[floorplanIndex].beds = requiredFloorplanErrorMessages.beds;
                isValidFloorplans = false;
            }

            if (!floorplan.baths) {
                updateErrorObj.floorplans[floorplanIndex].baths = requiredFloorplanErrorMessages.baths;
                isValidFloorplans = false;
            }

            if (validateUnits) {
                floorplan.units.forEach((unit, index) => {
                    if (!unit.name) {
                        updateErrorObj.floorplans[floorplanIndex].units[index].name = requiredUnitErrorMessages.name;
                        isValidFloorplans = false;
                    }
                });
            }
        });

        if (!isValidFloorplans) {
            this.setState({ errorObj: updateErrorObj });
        }

        // check discriminatory content errors for floorplan photo caption and floorplan amenities
        errorObj.floorplans.forEach((floorplan) => {
            if (floorplan.photoCaption || floorplan.amenities) {
                isValidFloorplans = false;
            }
        });

        return isValidFloorplans;
    }

    validateUnit(floorplanIndex, unitIndex) {
        const { formData, errorObj } = this.state;
        const updateErrorObj = cloneDeep(errorObj);
        let isValidUnit = true;
        const floorplan = formData.floorplans[floorplanIndex];
        const unit = floorplan && floorplan.units[unitIndex];
        if (unit) {
            if (!unit.name) {
                updateErrorObj.floorplans[floorplanIndex].units[unitIndex].name = requiredUnitErrorMessages.name;
                isValidUnit = false;
            }
        }

        if (!isValidUnit) {
            this.setState({ errorObj: updateErrorObj });
        }

        return isValidUnit;
    }

    updateActivationErrors(refName, message) {
        const error = {
            ref: refName,
            message,
        };
        this.state.activationErrors.errors.push(error);
    }

    validateFields(scrollToRequiredField = false) {
        const {
            formData,
            errorObj,
            activationErrors: previousActivationErrors,
        } = this.state;

        const { isCommunity, restrictions } = formData;
        const { applications } = restrictions;
        const isApplicationsRestricted = applications.isUpdateRestricted;

        let foundErrors = false;
        const hasAtLeastOneActiveFloorplan = isCommunity ? this.getActiveFloorplanOrUnitCount() >= 1 : true;
        const updateErrorObj = { ...errorObj };
        const hadActivationErrors = previousActivationErrors.length > 0;
        const requiredFields = isCommunity ? [...multiFamilyRequiredFields] : [...singleFamilyRequiredFields];

        // Skip validation for feed listings
        if (this.props.currentProperty.isFeed) {
            return false;
        }

        // remove validation if not in experiment
        if (!isApplicationsRestricted) {
            requiredFields.push(requiredFieldNames.isApplicationsAccepted);
        }
        const floorplanActivationErrors = [];
        const activationErrors = [];

        formData.floorplans.forEach((floorplan, index) => {
            if (floorplan.beds === '' || typeof floorplan.beds === 'undefined') {
                updateErrorObj.floorplans[index].beds = requiredFloorplanErrorMessages.beds;
                floorplanActivationErrors.push(
                    {
                        ref: requiredFieldNames.floorplans,
                        message: requiredFloorplanErrorMessages.beds,
                    }
                );
                foundErrors = true;
            }

            if (!floorplan.baths || floorplan.baths === '' || floorplan.baths < 1) {
                updateErrorObj.floorplans[index].baths = requiredFloorplanErrorMessages.baths;
                floorplanActivationErrors.push(
                    {
                        ref: requiredFieldNames.floorplans,
                        message: requiredFloorplanErrorMessages.baths,
                    }
                );
                foundErrors = true;
            }

            if (isCommunity) {
                if (!hasAtLeastOneActiveFloorplan) {
                    updateErrorObj.floorplans[index].active = requiredFloorplanErrorMessages.active;
                    floorplanActivationErrors.push(
                        {
                            ref: requiredFieldNames.floorplans,
                            message: requiredFloorplanErrorMessages.active,
                        }
                    );
                    foundErrors = true;
                }
                if (!floorplan.name) {
                    updateErrorObj.floorplans[index].name = requiredFloorplanErrorMessages.name;
                    floorplanActivationErrors.push(
                        {
                            ref: requiredFieldNames.floorplans,
                            message: requiredFloorplanErrorMessages.name,
                        }
                    );
                    foundErrors = true;
                }
                floorplan.units.forEach((unit, unitIndex) => {
                    if (!hasAtLeastOneActiveFloorplan) {
                        updateErrorObj.floorplans[index].units[unitIndex].active = requiredUnitErrorMessages.active;
                        floorplanActivationErrors.push(
                            {
                                ref: requiredFieldNames.floorplans,
                                message: requiredUnitErrorMessages.active,
                            }
                        );
                        foundErrors = true;
                    }
                    if (!unit.name) {
                        updateErrorObj.floorplans[index].units[unitIndex].name = requiredUnitErrorMessages.name;
                        floorplanActivationErrors.push(
                            {
                                ref: requiredFieldNames.floorplans,
                                message: requiredUnitErrorMessages.name,
                            }
                        );
                        foundErrors = true;
                    }
                });
            }
        });
        requiredFields.forEach((requiredField) => {
            if (requiredField === requiredFieldNames.floorplans) {
                activationErrors.push(...floorplanActivationErrors);
            } else if (formData.hasOwnProperty(requiredField)) {
                const message = requiredPropertyErrorMessages[requiredField];

                // for isApplicationsAccepted to be invalid it must be prestine
                // false is an acceptable value
                const invalidIsApplicationAccepted = requiredField === requiredFieldNames.isApplicationsAccepted
                    && formData[requiredField] === null;

                if (requiredField === requiredFieldNames.contactPhone && updateErrorObj[requiredField]) {
                    // Just because formData.contactPhone is filled out doesn't mean it has been validated via API.
                    // Need to check errorObj.contactPhone to see if any errors are present first.
                    // <ContactInfo /> will handle validating phone numbers through API and clearing errors.
                    activationErrors.push(
                        {
                            ref: requiredField,
                            message,
                        }
                    );
                    foundErrors = true;
                } else if (requiredField === requiredFieldNames.photos
                    && formData[requiredField].length < minPhotosMFH) {
                    updateErrorObj[requiredField] = message;
                    activationErrors.push(
                        {
                            ref: requiredField,
                            message,
                        }
                    );
                    foundErrors = true;
                } else if (
                    formData[requiredField] === '' ||
                    formData[requiredField] === undefined ||
                    invalidIsApplicationAccepted
                ) {
                    updateErrorObj[requiredField] = message;
                    activationErrors.push(
                        {
                            ref: requiredField,
                            message,
                        }
                    );
                    foundErrors = true;
                }
            } else if (requiredField === requiredFieldNames.price && formData.floorplans[0].price === '') {
                updateErrorObj.floorplans[0].price = requiredPropertyErrorMessages.price;
                activationErrors.push(
                    {
                        ref: requiredField,
                        message: requiredPropertyErrorMessages.price,
                    }
                );
                foundErrors = true;
            }
        });

        // discriminatory content
        if (this.validateDiscriminatoryContentErrors(scrollToRequiredField)) {
            foundErrors = true;
        }

        // todo: also need to check error object since results from api can affect things.
        this.setState({
            errorObj: updateErrorObj,
            activationErrors,
        });
        const hasActivationErrors = activationErrors.length > 0;
        if (scrollToRequiredField && hasActivationErrors) {
            this.scrollToRef(activationErrors[0].ref);
        }
        if (hadActivationErrors && !foundErrors) {
            this.setState({
                showReadyToPublishMessage: true,
            });
        }
        return foundErrors;
    }

    validateDiscriminatoryContentErrors(showNotification = true) {
        const {
            formData,
            errorObj,
        } = this.state;

        const { isCommunity } = formData;

        const discriminatoryContentFields = isCommunity ?
            [...multiFamilyDiscriminatoryContentCheckFields] : [...singleFamilyDiscriminatoryContentCheckFields];

        let foundErrors = false;

        discriminatoryContentFields.forEach((discriminatoryContentField) => {
            if (discriminatoryContentField === discriminatoryContentCheckFieldNames.floorplans
                && errorObj[discriminatoryContentField]) {
                errorObj[discriminatoryContentField].forEach((floorplan) => {
                    if (floorplan.name || floorplan.photoCaption || floorplan.amenities) {
                        foundErrors = true;
                    }
                });
            } else if (errorObj[discriminatoryContentField]) {
                foundErrors = true;
            }
        });
        if (foundErrors && showNotification) {
            this.updateStatusMessage('Please fix the inputs highlighted in red.', NotificationConstants.ERROR);
        }
        return foundErrors;
    }

    getActiveFloorplanOrUnitCount() {
        let activeCount = 0;
        this.state.formData.floorplans.forEach((floorplan) => {
            floorplan.units.forEach((unit) => {
                if (unit.active) {
                    activeCount += 1;
                }
            });
            if (floorplan.units.length === 0 && floorplan.active) {
                activeCount += 1;
            }
        });

        return activeCount;
    }

    handleEnableApplications = () => {
        const { paths } = this.context;
        const { dispatch } = this.props;
        const { propertyId } = this.state;

        // accept terms
        dispatch(TermsAndConditionsActions.markAcceptedTerms());
        this.dispatchGAEvent(GA_EVENTS.APPLICATIONS_POST_ACTIVATION_MODAL_ENABLE);

        const dataToSave = {
            propertyId,
            fieldName: 'applicationsAccepted',
            value: true,
        };

        return dispatch(PostingActions.property.edit(dataToSave))
            .then((response) => {
                if (response.success) {
                    window.router.updateLocation(`${paths.propertiesPage}/${propertyId}/applications`);
                    dispatch(ModalActions.hideModal());
                } else {
                    throw new Error(response.error.message);
                }
            })
            .catch(() => {
                const clientErrMsg = 'enableApplicationsError';
                dispatch(track.GAEventV2(
                    AnalyticsConstants.GA_EVENTS.HPPP_NEW_LISTING_ERROR,
                    {
                        action: startCaseRemoveSpace([clientErrMsg, propertyId].join(DELIMITER)),
                    }
                ));
                appLogger.logError({
                    message: clientErrMsg,
                    context: 'PostActivationModal.handleSubmit',
                });
            });
    };

    handleEnableLeases = () => {
        const { paths } = this.context;
        const { propertyId } = this.state;

        this.props.dispatch(track.GAEventV2(GA_EVENTS.LEASES_POST_ACTIVATION_MODAL_ENABLE));
        const url = `${paths.base}/leasing/property/${propertyId}/?itc=PostActivationModalLeases`;
        window.router.updateLocation(url);
    };

    handlePublishClick = (isFreeListing = false) => {
        if (this.isDisplayingFeedConnectPreActivateModal()) {
            return;
        }

        const { paths } = this.context;
        const {
            currentProperty,
            defaultPaymentMethod,
            dispatch,
            listing,
            user,
        } = this.props;

        const { paidInclusion } = listing;
        const { price } = paidInclusion;
        const { isPaymentValid } = this.state;
        const { leadGuarantee } = currentProperty;
        const { isEligible: isEligibleForLeadGuarantee } = leadGuarantee;

        // feed or already paid for
        if (isFreeListing || isPaymentValid) {
            if (isPaymentValid) {
                dispatch(PropertiesActions.updateCurrentProperty({ autoBill: true }));
            }

            return this.handleSubmit(listing.paidInclusion);
        }

        this.setState({
            activateButtonClicked: true,
        });

        if (this.validateFields(true)) {
            return;
        }

        dispatch(ModalActions.showModal({
            modalType: ModalConstants.PAID_INCLUSION_PAYMENT_MODAL,
            handleVerifyPaymentAndActivateListingResponse: this.handleVerifyPaymentAndActivateListingResponse,
            shouldCloseOnOverlayClick: false,
            listing,
            user,
            price,
            paths,
            isRenderedInPageView: false,
            isEligibleForLeadGuarantee,
            onOpen: () => this.dispatchGAEvent(GA_EVENTS.CHECKOUT_BEGIN),
            onClose: () => {
                // if isCharged is true, user did not dismiss checkout modal.
                const { isCharged } = this.props;
                if (isCharged) {
                    return;
                }

                this.dispatchGAEvent(GA_EVENTS.CHECKOUT_DISMISSED);
            },
            onChargeFail: () => this.dispatchGAEvent(GA_EVENTS.CHECKOUT_FAILURE),
            onPaymentMethodChange: (method) => {
                if (method === PaidInclusionConstants.PAYMENT_METHOD.NEW) {
                    this.dispatchGAEvent(GA_EVENTS.SECONDARY_CHECKOUT_NEW_PAYMENT_METHOD);
                } else {
                    this.dispatchGAEvent(GA_EVENTS.SECONDARY_CHECKOUT_DEFAULT_PAYMENT_METHOD);
                }
            },
            header: 'Please provide your credit card information',
        }));

        // This is to check (get fresh value) if latest "pending" payment went through or not
        dispatch(PaidInclusionActions.verifyPaymentAndActivateListing(listing, !defaultPaymentMethod))
            .then(result => this.handleVerifyPaymentAndActivateListingResponse(result));
    };

    handleVerifyPaymentAndActivateListingResponse(verifyPaymentAndActivateListingResponse) {
        const { listing } = this.props;
        const { paidInclusion } = listing;
        const { error, response, success } = verifyPaymentAndActivateListingResponse;

        let activateListingPromise;
        if (success) {
            const { activation } = response;
            if (!activation) {
                return;
            }
            activation.success = true;
            activateListingPromise = Promise.resolve(activation);
            this.handleSubmit(paidInclusion, activateListingPromise, true);
            return;
        }
        if (error) {
            const { data } = error;
            const { listingActivationFailed, activationError } = data;
            if (listingActivationFailed) {
                activateListingPromise = Promise.reject(activationError);
                this.handleSubmit(paidInclusion, activateListingPromise, true);
            }
        }
    }

    getApplicationsEnabledStatus() {
        const { formData: { isApplicationsAccepted, restrictions } } = this.state;
        const canUpdateApplications = accessUtils.canUpdateApplications({ restrictions });

        if (canUpdateApplications) {
            return isApplicationsAccepted ? 'ApplicationsEnabled' : 'ApplicationsDisabled';
        }

        return 'ApplicationsIneligible';
    }

    getUpsellType() {
        const {
            formData,
            canUpdateApplications,
        } = this.state;

        // accepting terms are handled on the back end
        const isApplicationsToggleEnabled = this.props.userApplicationsEnabled && formData.isApplicationsAccepted;
        if (canUpdateApplications && !isApplicationsToggleEnabled) {
            return 'Applications';
        }
        if (accessUtils.canAccessLeaseBuilder({ restrictions: formData.restrictions })) {
            return 'Leases';
        }
        return 'None';
    }

    // TODO: refactor handleSubmit to accept an object as an argument
    // eliminate the need to pass arguments in a specific order
    // add skipFormVerify as argument to deconstruct
    handleSubmit(
        { isEnabled: isInPaidMarket, isPaymentRequired } = {},
        activateListingPromise = undefined,
        isPaidActivation = false
    ) {
        const {
            propertyId,
        } = this.state;
        const {
            currentProperty,
            dispatch,
            isScreenXS,
            isScreenSM,
            location,
            isEditListingFocused,
            listing,
        } = this.props;
        const { paidInclusion = {}, singleFamilyContract } = listing;
        const { price = '' } = paidInclusion;
        const { isFeed } = currentProperty;
        const upsellType = this.getUpsellType();

        this.setState({
            activateButtonClicked: true,
        });

        if (this.validateFields(true)) {
            // found error
            return;
        }

        if (!activateListingPromise) {
            activateListingPromise = dispatch(PostingActions.property.activate(propertyId, false));
        }

        activateListingPromise
            .then((result) => {
                if (result.success === true) {
                    dispatch(PropertiesActions.updateCurrentProperty({ active: true }));

                    // quit focused edit listing mode
                    if (isEditListingFocused && location.search) {
                        window.router.transitionTo({
                            pathname: location.pathname,
                            search: '',
                        });
                    } else {
                        window.scrollTo(0, 0);
                    }

                    // set syndication statuses to pending.
                    const { formData: { syndication } } = this.state;
                    const syndicationCopy = {
                        ...syndication,
                        zillow: {
                            displayStatus: Constants.LISTING_STATUS.PENDING,
                        },
                        hotpads: {
                            displayStatus: Constants.LISTING_STATUS.PENDING,
                        },
                        trulia: {
                            displayStatus: Constants.LISTING_STATUS.PENDING,
                        },
                        overview: {
                            status: SYNDICATION_OVERVIEW_STATUS.PUBLISHING,
                            displayStatus: LISTING_STATUS.PUBLISHING,
                            publishedCount: 0,
                        },
                    };

                    // update state
                    this.setState({
                        formErrorMessage: '',
                        syndication: syndicationCopy,
                    });

                    this.updateFormData({ active: true, syndication: syndicationCopy });
                } else {
                    throw result.error;
                }
            })
            .then(() => dispatch(PostingActions.property.getStatus(propertyId)).then((response) => {
                const { expirationDate } = response;
                const formattedExpirationDate = expirationDate ? format(expirationDate, 'MMM D, YYYY') : '-';

                this.updateFormData({ expirationDate: formattedExpirationDate });

                dispatch(ModalActions.showModal({
                    modalType: ModalConstants.POST_ACTIVATION_MODAL,
                    isInPaidMarket,
                    expirationDate: formattedExpirationDate,
                    isListingFree: !isPaymentRequired,
                    price,
                    upsellType,
                    onEnableApplications: this.handleEnableApplications,
                    onEnableLeases: this.handleEnableLeases,
                    onOpen: () => {
                        const addPay = isInPaidMarket && isPaidActivation;
                        window.dataLayer.push({
                            event: `RentalManager${DELIMITER}${addPay ? 'Pay' : ''}Publish`,
                        });

                        if (singleFamilyContract.isRequired) {
                            const actionPrefix = GA_EVENTS.ACTIVATE_LISTING_SF_ENTERPRISE.action
                                + (isFeed ? 'Feed' : 'Manual');
                            const gaEvent = {
                                ...GA_EVENTS.ACTIVATE_LISTING_SF_ENTERPRISE,
                                action: appendSuffix(actionPrefix, this.getApplicationsEnabledStatus(), true),
                            };
                            this.dispatchGAEvent(gaEvent);
                        } else if (!isInPaidMarket) {
                            const gaEvent = {
                                ...GA_EVENTS.ACTIVATE_LISTING_NON_NUCLEUS,
                                action: appendSuffix(GA_EVENTS.ACTIVATE_LISTING_NON_NUCLEUS.action,
                                    this.getApplicationsEnabledStatus(),
                                    true),
                            };
                            this.dispatchGAEvent(gaEvent);
                        } else if (!isPaymentRequired) {
                            const gaEvent = {
                                ...GA_EVENTS.ACTIVATE_LISTING_NUCLEUS_FREE,
                                action: appendSuffix(GA_EVENTS.ACTIVATE_LISTING_NUCLEUS_FREE.action,
                                    this.getApplicationsEnabledStatus(),
                                    true),
                            };
                            this.dispatchGAEvent(gaEvent);
                        } else if (isPaidActivation) {
                            const gaEvent = {
                                ...AnalyticsConstants.GA_EVENTS.ACTIVATE_LISTING_NUCLEUS_PAID,
                                action: appendSuffix(
                                    AnalyticsConstants.GA_EVENTS.ACTIVATE_LISTING_NUCLEUS_PAID.action,
                                    this.getApplicationsEnabledStatus(),
                                    true,
                                ),
                                value: Math.round(price.replace('$', '')),
                            };

                            this.dispatchGAEvent(gaEvent);
                        } else {
                            this.dispatchGAEvent(GA_EVENTS.REACTIVATE_WITHIN_WINDOW);
                        }

                        if (upsellType === 'Leases') {
                            dispatch(track.GAEventV2(GA_EVENTS.LEASES_POST_ACTIVATION_MODAL_DISPLAY));
                        }
                    },
                    onClose: () => {
                        if (upsellType === 'Applications') {
                            this.dispatchGAEvent(GA_EVENTS.APPLICATIONS_POST_ACTIVATION_MODAL_DISMISS);
                        }
                        if (upsellType === 'Leases') {
                            dispatch(track.GAEventV2(GA_EVENTS.LEASES_POST_ACTIVATION_MODAL_DISMISS));
                        }
                        if (!isScreenXS && !isScreenSM) {
                            setTimeout(() => {
                                NotificationModule.doPermissionChecks();
                            }, NOTIFICATION_OPTION.PRE_PROMPT_DELAY);
                        }
                    },

                }));
            }))
            .catch((errorRMWModel) => {
                dispatch(ModalActions.showModal({
                    modalType: ModalConstants.ERROR_MODAL,
                    header: 'Error',
                    error: errorRMWModel,

                }));

                appLogger.logError({
                    message: `'HPPP_ACTIVATION_ERROR: ${errorRMWModel}`,
                    context: 'EditListingPage.handleSubmit',
                });
            });
    }

    openAddPropertyModal() {
        const { dispatch } = this.props;

        // error message will be set within modal
        // for some reason I was getting an infinite setState loop
        // when trying to pass the error as a prop
        dispatch(
            ModalActions.updateDisplayAddPropertyModal({
                isOpen: true,
            }),
        );
    }

    showSiftScience(email, token) {
        // Since admins and reps can edit / view other user listings, we'll hide Sift Science
        // tools so it doesn't send along erroneous user data.
        const { impersonatorUserToken } = this.props;

        // user is impersonating
        if (impersonatorUserToken !== '') {
            return false;
        }

        if (typeof window !== 'undefined') {
            // Import SiftScience script
            const siftScienceScript = document.createElement('script');
            siftScienceScript.src = 'https://cdn.siftscience.com/s.js';
            siftScienceScript.setAttribute('id', 'sift-science');
            document.body.appendChild(siftScienceScript);

            // Setup SiftScience tracker
            const siftScienceTracker = document.createElement('script');
            siftScienceTracker.setAttribute('id', 'sift-science-tracker');
            siftScienceTracker.type = 'text/javascript';
            const codeToAppend = `
                var _user_id = '${email}';
                var _session_id = '${token}';

                var _sift = window._sift = window._sift || [];
                _sift.push(['_setAccount', '4f0e3321cc']);
                _sift.push(['_setUserId', _user_id]);
                _sift.push(['_setSessionId', _session_id]);
                _sift.push(['_trackPageview']);
            `;

            try {
                siftScienceTracker.appendChild(document.createTextNode(codeToAppend));
                document.body.appendChild(siftScienceTracker);
            } catch (e) {
                siftScienceTracker.text = codeToAppend;
                document.body.appendChild(siftScienceTracker);
            }
        }

        return true;
    }

    scrollToRef(refName) {
        const ref = this.refMap.get(refName);
        let { current } = ref;
        let offset = -30;

        while (current != null) {
            offset += current.offsetTop;
            current = current.offsetParent;
        }

        if (this.activationErrorsRef.current != null) {
            offset -= this.activationErrorsRef.current.clientHeight;
        }
        window.scrollTo({
            top: offset,
            behavior: 'smooth',
        });
    }

    renderActivationErrors(activationErrors) {
        // if screen is small, render as a sticky. else render as a clickable toast.
        if (activationErrors.length === 0) {
            return <div />;
        }

        return (
            <div>
                <StyledActivationErrorStickyDiv ref={this.activationErrorsRef}>
                    <Sticky>
                        {({ isSticky }) => (
                            <div
                                className={isSticky ? 'sticky-Activation-Error-Box' : 'Activation-Error-Box'}
                            >
                                <ul>
                                    {activationErrors.slice(0, 3).map((error, i) => (
                                        <li style={{ paddingTop: '8px', paddingBottom: '8px' }} key={i}>
                                            <TextButton
                                                inverse={true}
                                                onClick={() => this.scrollToRef(error.ref)}
                                            >
                                                {error.message}
                                            </TextButton>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </Sticky>
                </StyledActivationErrorStickyDiv>
                <StyledActivationErrorToastDiv>
                    <StyledToast
                        className={cx({
                            'Toast-Base': true,
                            'Toast-Wrapper-hidden': false,
                        })}

                    >
                        <TextButton
                            inverse={true}
                            onClick={() => this.scrollToRef(activationErrors[0].ref)}
                        >
                            {activationErrors[0].message}
                        </TextButton>
                    </StyledToast>
                </StyledActivationErrorToastDiv>
            </div>
        );
    }

    renderlistingDetails() {
        const {
            canUpdateApplications,
            formData,
            errorObj,
            propertyId,
            statusMessage,
            isListingPhotoProcessing,
            isAddingUnit,
            activateButtonClicked,
            activationErrors,
            isPaymentValid,
        } = this.state;

        const { isArchived } = this.props;
        const isInputFormDisabled = isArchived || formData.restrictions.isUpdateListingRestricted;
        const { isCommunity } = formData;

        return (
            <StyledPostingFormDiv>
                <StyledMainContentDiv>
                    <StickyContainer>
                        <div className="listing-details">
                            {activateButtonClicked && this.renderActivationErrors(activationErrors)}
                            {isCommunity ? (
                                <MultiFamilyForm
                                    contactNameRef={this.contactNameRef}
                                    contactPhoneRef={this.contactPhoneRef}
                                    descriptionRef={this.descriptionRef}
                                    errorObj={errorObj}
                                    floorplansRef={this.floorplansRef}
                                    formData={formData}
                                    forRentByRef={this.forRentByRef}
                                    getActiveFloorplanOrUnitCount={this.getActiveFloorplanOrUnitCount}
                                    immediateUpdateAndSave={this.immediateUpdateAndSave}
                                    isAddingUnit={isAddingUnit}
                                    isInputFormDisabled={isInputFormDisabled}
                                    isListingPhotoProcessing={isListingPhotoProcessing}
                                    listingTitleRef={this.listingTitleRef}
                                    photosRef={this.photosRef}
                                    propertyId={propertyId}
                                    saveFloorplanData={this.saveFloorplanData}
                                    savePropertyData={this.savePropertyData}
                                    statusMessage={statusMessage}
                                    updateErrorObj={this.updateErrorObj}
                                    updateFormData={this.updateFormData}
                                    updateListingPhotoProcessingStatus={this.updateListingPhotoProcessingStatus}
                                    updateOnFocus={this.updateOnFocus}
                                    updateParentFloorplanPrice={this.updateParentFloorplanPrice}
                                    updateStatusMessage={this.updateStatusMessage}
                                    validateFloorplan={this.validateFloorplan}
                                    validateFloorplans={this.validateFloorplans}
                                    validateUnit={this.validateUnit}
                                />
                            ) : (
                                <SingleFamilyForm
                                    canUpdateApplications={canUpdateApplications}
                                    contactNameRef={this.contactNameRef}
                                    contactPhoneRef={this.contactPhoneRef}
                                    descriptionRef={this.descriptionRef}
                                    errorObj={errorObj}
                                    floorplansRef={this.floorplansRef}
                                    formData={formData}
                                    forRentByRef={this.forRentByRef}
                                    immediateUpdateAndSave={this.immediateUpdateAndSave}
                                    isApplicationsAcceptedRef={this.isApplicationsAcceptedRef}
                                    isInputFormDisabled={isInputFormDisabled}
                                    isListingPhotoProcessing={isListingPhotoProcessing}
                                    leaseDurationRef={this.leaseDurationRef}
                                    rentPriceRef={this.rentPriceRef}
                                    saveFloorplanData={this.saveFloorplanData}
                                    savePropertyData={this.savePropertyData}
                                    statusMessage={statusMessage}
                                    updateErrorObj={this.updateErrorObj}
                                    updateFormData={this.updateFormData}
                                    updateListingPhotoProcessingStatus={this.updateListingPhotoProcessingStatus}
                                    updateOnFocus={this.updateOnFocus}
                                    updateStatusMessage={this.updateStatusMessage}
                                    isPaymentValid={isPaymentValid}
                                    setSuccessfulRenewalData={this.setSuccessfulRenewalData}
                                    setFailedRenewalData={this.setFailedRenewalData}
                                />
                            )}
                        </div>
                    </StickyContainer>
                </StyledMainContentDiv>
            </StyledPostingFormDiv>
        );
    }

    renderNotViewableContent() {
        const { errorObj, formData } = this.state;

        return (
            <NotViewableContent
                errorObj={errorObj}
                formData={formData}
                getActiveFloorplanOrUnitCount={this.getActiveFloorplanOrUnitCount}
                isAddingUnit={this.isAddingUnit}
                isArchived={this.isArchived}
                isListingPhotoProcessing={this.isListingPhotoProcessing}
                saveFloorplanData={this.saveFloorplanData}
                updateErrorObj={this.updateErrorObj}
                updateFormData={this.updateFormData}
                updateListingPhotoProcessingStatus={this.updateListingPhotoProcessingStatus}
                updateOnFocus={this.updateOnFocus}
                updateParentFloorplanPrice={this.updateParentFloorplanPrice}
                updateStatusMessage={this.updateStatusMessage}
                validateFloorplan={this.validateFloorplan}
                validateFloorplans={this.validateFloorplans}
                validateUnit={this.validateUnit}
            />
        );
    }

    renderRestrictionAddressMessageAlert() {
        const { errorMessage } = this.state;

        return (
            <Alert appearance="warning" body={errorMessage} closeButton={<CloseButton />} />
        );
    }

    render() {
        const {
            appConfig,
            cookieEnabledServices,
        } = this.props;
        const {
            formData,
            savedStatus,
            statusMessage,
            isLoading,
            hasRestrictionErrorMessage,
            paidListingExists,
            activationErrors,
        } = this.state;
        const {
            isCommunity,
            isViewable,
            topDupesDetails,
            feedName,
            feedId,
            isFeed,
        } = formData;
        const notificationType = this.getNotificationType();

        // Show dupes section if current listing is promoted listing and there are dupes to show
        const showDupeDetails = topDupesDetails && !!topDupesDetails.dupes.length;
        let titleContent = 'Edit Listing';
        if (formData.listingTitle && isCommunity) {
            titleContent = `Edit ${formData.listingTitle}`;
        } else if (formData.street) {
            titleContent = `Edit ${formData.street}`;
        }

        // if activation button was clicked and there are activation errors, hide notifications since activation
        // error sticky will be active.
        const hideNotification = this.state.activateButtonClicked && activationErrors.length > 0;
        const canFeedConnectSwitchSource = accessUtils.canFeedConnectSwitchSource({ appConfig, cookieEnabledServices });

        if (isLoading) {
            return (
                <PDPContentSkeleton />
            );
        }
        return (
            <div className="EditListingPage">
                {!isViewable && !canFeedConnectSwitchSource && (
                    <NotViewableMessage
                        feedId={feedId}
                        feedName={feedName}
                    />
                )}
                {hasRestrictionErrorMessage && (
                    <Spacer marginBottom="md">
                        {this.renderRestrictionAddressMessageAlert()}
                    </Spacer>
                )}
                {canFeedConnectSwitchSource && <FeedListingSourceBanner formData={formData} />}
                <div className="EditListingPage-Container">
                    <Row paddingY="xs">
                        <Col xs={12}>
                            <HeadWrapper title={titleContent} />
                            {statusMessage && (
                                <StyledNotificationToastContainerDiv>
                                    { !hideNotification && (
                                        <StyledNotification
                                            iconClose={false}
                                            type={notificationType}
                                            className={cx({
                                                'Notification-opacity': statusMessage.length > 0,
                                                'Notification-done': savedStatus === NotificationConstants.SUCCESS,
                                            })}
                                        >
                                            {statusMessage}
                                        </StyledNotification>
                                    )}

                                    <StyledToast
                                        className={cx({
                                            'Toast-Base': true,
                                            'Toast-Wrapper-hidden':
                                                    savedStatus === NotificationConstants.SUCCESS,
                                        })}
                                    >
                                        {statusMessage}
                                    </StyledToast>
                                </StyledNotificationToastContainerDiv>
                            )}
                            {showDupeDetails && !canFeedConnectSwitchSource && (
                                <DuplicateListingDetails topDupesDetails={topDupesDetails} isFeed={isFeed} />
                            )}
                            {isViewable
                                ? this.renderlistingDetails()
                                : this.renderNotViewableContent()
                            }
                        </Col>
                    </Row>
                </div>
                <ConnectedListingFooter
                    canAccessPayments={this.props.canAccessPayments}
                    formData={formData}
                    hasRestrictionErrorMessage={hasRestrictionErrorMessage}
                    onPublishClick={this.handlePublishClick}
                    paidListingExists={paidListingExists}
                    updateFormData={this.updateFormData}
                />
                <ListingActivationTerms
                    isTextCenter={true}
                    actionText={`By clicking ${formData.active ? 'Deactivate' : 'Publish'} listing`}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({
    appConfig: AppSelectors.makeAppConfig(state),
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
    clientLoaded: AppSelectors.makeClientLoaded(state),
    email: UserSelectors.makeEmail(state),
    userApplicationsEnabled: UserSelectors.makeApplicationsEnabled(state),
    user: UserSelectors.makeUser(state),
    impersonatorUserToken: UserSelectors.makeImpersonatorUserToken(state),
    isScreenXS: AppSelectors.makeIsScreenXS(state),
    isScreenSM: AppSelectors.makeIsScreenSM(state),
    currentProperty: PropertiesSelectors.makeCurrentProperty(state),
    isCharged: PaidInclusionSelectors.makeIsCharged(state),
    isPaymentValid: PaidInclusionSelectors.makeIsPaymentValid(state),
    defaultPaymentMethod: PaidInclusionSelectors.makeDefaultPaymentMethod(state),
    paidInclusionListingCannotBeActivatedError: PaidInclusionSelectors.makeListingCannotBeActivatedError(state),
    propertyId: RoutingSelectors.makeListingId(state),
    search: RoutingSelectors.makeSearch(state),
});

EditListingPage.propTypes = propTypes;
EditListingPage.defaultProps = defaultProps;

export { EditListingPage };
export default connect(mapStateToProps)(EditListingPage);
